// F37, I24, R28 (Fabi, Runde 7): Behutsamer Zufall. Werte liegen in einem Rahmen; Ausreißer nach oben oder unten sind
// seltener als die Mitte, kommen aber vor. Alles läuft über den seeded RNG (X2).
import { makeItemRef, makeStarRef, runRules, systemOn, type RunRules } from '../content/index.ts';
import type { Rng } from './rng.ts';

/** Dreieckig verteilt zwischen −1 und 1: die Mitte am häufigsten, die Ränder selten */
export function bell(rng: Rng): number {
  return rng.next() + rng.next() - 1;
}

/** Ganze Zahl im Rahmen [lo, hi], die Mitte am häufigsten */
export function rollRange(rng: Rng, range: number | readonly number[]): number {
  if (typeof range === 'number') return range;
  const [lo, hi] = [range[0]!, range[1] ?? range[0]!];
  return Math.round(lo + ((bell(rng) + 1) / 2) * (hi - lo));
}

/** Anteil im Rahmen [lo, hi] (etwa Heilung 7 bis 17 %), die Mitte am häufigsten */
export function rollShare(rng: Rng, range: number | readonly number[]): number {
  if (typeof range === 'number') return range;
  const [lo, hi] = [range[0]!, range[1] ?? range[0]!];
  return lo + ((bell(rng) + 1) / 2) * (hi - lo);
}

/** R28 (Fabi): ganze Zahl im Rahmen, je mehr, desto seltener (Holz pro Sieg 1 bis 4: 1 am häufigsten) */
export function rollLow(rng: Rng, range: readonly number[]): number {
  const [lo, hi] = [range[0]!, range[1] ?? range[0]!];
  const n = hi - lo + 1;
  const total = (n * (n + 1)) / 2;
  let r = rng.next() * total;
  for (let k = 0; k < n; k++) {
    r -= n - k;
    if (r < 0) return lo + k;
  }
  return hi;
}

export type FriendRolls = Partial<Record<keyof RunRules['luck']['friend'], number>>;

/** F37 (Fabi, Runde 7): Anlagen eines neuen Freundes, je Wert zwischen −1 und 1 */
export function rollFriend(rng: Rng, rules: RunRules = runRules): FriendRolls {
  const out: FriendRolls = {};
  for (const key of Object.keys(rules.luck.friend) as (keyof RunRules['luck']['friend'])[]) out[key] = Math.round(bell(rng) * 100) / 100;
  return out;
}

/**
 * I24: ein neues Item mit gewürfeltem Wert. I27 (Fabi, Runde 8): Mit etwas Glück ist es besonders (★) und 17 bis 23 %
 * stärker; starFactor erhöht die Chance (etwa mit der Elster an Bord, S8).
 */
export function newItem(rng: Rng, id: string, starFactor = 1, rules: RunRules = runRules): string {
  if (rng.next() < rules.luck.itemStarChance * starFactor) {
    const [lo, hi] = [rules.luck.itemStarRange[0]!, rules.luck.itemStarRange[1] ?? rules.luck.itemStarRange[0]!];
    return makeStarRef(id, lo + Math.floor(rng.next() * (hi - lo + 1)));
  }
  // I30 (Fabi, Phase Kern): Streuung ausgeblendet, jedes gewöhnliche Stück hat die festen Zahlen der Mitte
  return makeItemRef(id, systemOn('itemSpread') ? bell(rng) : 0);
}
